import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  HStack,
  Spinner,
  Text,
  Textarea as ChakraTextarea,
  VStack,
} from "@chakra-ui/react";
import { Loader2 } from "lucide-react";
import { Button as UiButton } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { EditIcon, type EditIconHandle } from "../ui/edit";
import { TrashIcon, type TrashIconHandle } from "../ui/trash";
import type { Note } from "../../types/notes";
import { DeleteNoteDialog } from "./DeleteNoteDialog";
import { ReportNotesComposer } from "./ReportNotesComposer";

export type NotesListEditorProps = {
  notes: Note[];
  loading?: boolean;
  error?: string | null;
  draft: string;
  onDraftChange: (value: string) => void;
  editingId: string | null;
  editingBody: string;
  onEditingBodyChange: (value: string) => void;
  saving: boolean;
  canSubmit: boolean;
  onCreate: () => void;
  onStartEdit: (note: Note) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onDelete: (noteId: string) => void | Promise<boolean>;
  borderColor: string;
  subtleText: string;
  textColor: string;
  borderRadius?: string;
  /** Hide note composer (read-only list). */
  showComposer?: boolean;
  /** Trade-details style shadcn textarea + gradient button (report tab notes). */
  composerVariant?: "chakra" | "shadcn";
  /** Shadcn field label, e.g. "Overview notes". */
  composerLabel?: string;
  emptyLabel?: string;
};

export function NotesListEditor(props: NotesListEditorProps): React.ReactElement {
  const radius = props.borderRadius ?? "6px";
  const showComposer = props.showComposer !== false;
  const composerVariant = props.composerVariant ?? "chakra";
  const composerFieldId = useId();
  const showEmptyInline =
    showComposer && props.notes.length === 0 && !props.loading && !props.error;
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [isDeletingNote, setIsDeletingNote] = useState(false);

  const closeDeleteDialog = useCallback(() => {
    if (isDeletingNote) return;
    setPendingDeleteId(null);
  }, [isDeletingNote]);

  const confirmDelete = useCallback(async () => {
    if (!pendingDeleteId || isDeletingNote) return;
    setIsDeletingNote(true);
    try {
      const result = await props.onDelete(pendingDeleteId);
      if (result !== false) setPendingDeleteId(null);
    } finally {
      setIsDeletingNote(false);
    }
  }, [isDeletingNote, pendingDeleteId, props]);

  return (
    <VStack align="stretch" spacing={3}>
      <DeleteNoteDialog
        open={pendingDeleteId != null}
        onOpenChange={(open) => {
          if (!open) closeDeleteDialog();
        }}
        onConfirm={() => void confirmDelete()}
        isDeleting={isDeletingNote}
      />
      {showComposer ? (
        composerVariant === "shadcn" ? (
          <ReportNotesComposer
            fieldId={composerFieldId}
            label={props.composerLabel}
            value={props.draft}
            onChange={props.onDraftChange}
            canSubmit={props.canSubmit}
            saving={props.saving}
            onSubmit={props.onCreate}
            showEmptyHint={showEmptyInline}
            emptyHint={props.emptyLabel}
          />
        ) : (
          <Box>
            <ChakraTextarea
              value={props.draft}
              onChange={(e) => props.onDraftChange(e.target.value)}
              placeholder="Type your note here…"
              minH="96px"
              resize="vertical"
              borderRadius={radius}
              fontFamily="'Urbanist', sans-serif"
              fontSize="sm"
              fontWeight="normal"
              color={props.subtleText}
            />
            <HStack justify={showEmptyInline ? "space-between" : "flex-end"} align="center" mt={2} w="100%" gap={3}>
              {showEmptyInline ? (
                <Text fontSize="sm" color={props.subtleText} flex="1" minW={0}>
                  {props.emptyLabel ?? "No notes yet."}
                </Text>
              ) : null}
              <Button
                size="sm"
                flexShrink={0}
                bgGradient="linear-gradient(135deg, #38BBD1, #3EEAEC)"
                color="white"
                borderRadius={radius}
                fontSize="xs"
                fontFamily="'IBM Plex Sans', sans-serif"
                fontWeight="medium"
                textTransform="uppercase"
                _hover={{ opacity: 0.9 }}
                onClick={props.onCreate}
                isDisabled={!props.canSubmit}
                isLoading={props.saving}
              >
                Add note
              </Button>
            </HStack>
          </Box>
        )
      ) : null}

      {showComposer && (props.loading || props.notes.length > 0 || props.error) ? (
        <Divider borderColor={props.borderColor} />
      ) : null}

      {props.loading ? (
        <HStack>
          <Spinner size="sm" />
          <Text fontSize="sm" color={props.subtleText}>
            Loading notes…
          </Text>
        </HStack>
      ) : props.error ? (
        <Alert status="error" borderRadius={radius}>
          <AlertIcon />
          <AlertDescription>{props.error}</AlertDescription>
        </Alert>
      ) : props.notes.length === 0 && !showComposer ? (
        <Text fontSize="sm" color={props.subtleText}>
          {props.emptyLabel ?? "No notes yet."}
        </Text>
      ) : props.notes.length > 0 ? (
        <VStack align="stretch" spacing={3}>
          {props.notes.map((n) => (
            <NoteListItem
              key={n.id}
              note={n}
              isEditing={props.editingId === n.id}
              editingBody={props.editingBody}
              onEditingBodyChange={props.onEditingBodyChange}
              saving={props.saving}
              borderRadius={radius}
              borderColor={props.borderColor}
              subtleText={props.subtleText}
              textColor={props.textColor}
              composerVariant={composerVariant}
              onStartEdit={props.onStartEdit}
              onCancelEdit={props.onCancelEdit}
              onSaveEdit={props.onSaveEdit}
              onRequestDelete={setPendingDeleteId}
            />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}

function NoteListItem(props: {
  note: Note;
  isEditing: boolean;
  editingBody: string;
  onEditingBodyChange: (value: string) => void;
  saving: boolean;
  borderRadius: string;
  borderColor: string;
  subtleText: string;
  textColor: string;
  composerVariant: "chakra" | "shadcn";
  onStartEdit: (note: Note) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onRequestDelete: (noteId: string) => void;
}): React.ReactElement {
  const editIconRef = useRef<EditIconHandle | null>(null);
  const deleteIconRef = useRef<TrashIconHandle | null>(null);

  const startEditIconAnimation = useCallback(() => {
    void editIconRef.current?.startAnimation();
  }, []);

  const stopEditIconAnimation = useCallback(() => {
    void editIconRef.current?.stopAnimation();
  }, []);

  const editIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startEditIconAnimation,
      onMouseEnter: startEditIconAnimation,
      onMouseLeave: stopEditIconAnimation,
    }),
    [startEditIconAnimation, stopEditIconAnimation]
  );

  const onEditClick = useCallback(() => {
    startEditIconAnimation();
    props.onStartEdit(props.note);
    window.setTimeout(() => {
      void editIconRef.current?.stopAnimation();
    }, 260);
  }, [props, startEditIconAnimation]);

  const startDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.startAnimation();
  }, []);

  const stopDeleteIconAnimation = useCallback(() => {
    void deleteIconRef.current?.stopAnimation();
  }, []);

  const deleteIconHoverHandlers = useMemo(
    () => ({
      onPointerDownCapture: startDeleteIconAnimation,
      onMouseEnter: startDeleteIconAnimation,
      onMouseLeave: stopDeleteIconAnimation,
    }),
    [startDeleteIconAnimation, stopDeleteIconAnimation]
  );

  const onDeleteClick = useCallback(() => {
    startDeleteIconAnimation();
    props.onRequestDelete(props.note.id);
    window.setTimeout(() => {
      void deleteIconRef.current?.stopAnimation();
    }, 260);
  }, [props, startDeleteIconAnimation]);

  return (
    <Box
      borderWidth="1px"
      borderRadius={props.borderRadius}
      borderColor={props.borderColor}
      p={3}
      data-note-card=""
    >
      <HStack justify="space-between" align="flex-start" gap={2} mb={2}>
        <p className="min-w-0 font-[family-name:var(--font-primary)] text-xs font-normal text-[color:var(--brand-tropical-surf)]">
          {props.note.created_at ? new Date(props.note.created_at).toLocaleString() : "Note"}
        </p>
        <HStack spacing={1} flexShrink={0}>
          <UiButton
            type="button"
            variant="outline"
            size="icon-xs"
            aria-label="Edit note"
            data-note-edit-button=""
            className="shrink-0"
            onClick={onEditClick}
            disabled={props.saving}
            {...editIconHoverHandlers}
          >
            <EditIcon ref={editIconRef} size={14} className="shrink-0 text-current" aria-hidden />
          </UiButton>
          <UiButton
            type="button"
            variant="outline"
            size="icon-xs"
            aria-label="Delete note"
            data-trade-log-action="delete"
            className="shrink-0"
            onClick={onDeleteClick}
            disabled={props.saving}
            {...deleteIconHoverHandlers}
          >
            <TrashIcon ref={deleteIconRef} size={14} className="shrink-0 text-current" aria-hidden />
          </UiButton>
        </HStack>
      </HStack>

      {props.isEditing ? (
        <VStack align="stretch" spacing={2}>
          {props.composerVariant === "shadcn" ? (
            <div data-shadcn-notes-composer="" className="w-full">
              <Textarea
                value={props.editingBody}
                onChange={(e) => props.onEditingBodyChange(e.target.value)}
                placeholder="Edit note…"
                aria-label="Edit note"
              />
            </div>
          ) : (
            <ChakraTextarea
              value={props.editingBody}
              onChange={(e) => props.onEditingBodyChange(e.target.value)}
              minH="120px"
              borderRadius={props.borderRadius}
              fontFamily="var(--font-primary)"
              fontSize="sm"
              fontWeight="normal"
              color={props.subtleText}
            />
          )}
          <HStack justify="flex-end">
            {props.composerVariant === "shadcn" ? (
              <>
                <UiButton
                  type="button"
                  variant="outline"
                  size="sm"
                  data-font="primary"
                  data-market-context-bulk-toggle=""
                  className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
                  onClick={props.onCancelEdit}
                  disabled={props.saving}
                >
                  Cancel
                </UiButton>
                <UiButton
                  type="button"
                  variant="gradient"
                  size="sm"
                  data-font="primary"
                  className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
                  onClick={() => void props.onSaveEdit()}
                  disabled={props.saving}
                >
                  {props.saving ? (
                    <>
                      <Loader2 className="size-4 animate-spin" aria-hidden />
                      <span>Saving…</span>
                    </>
                  ) : (
                    <span>Save</span>
                  )}
                </UiButton>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="ghost"
                  borderRadius={props.borderRadius}
                  onClick={props.onCancelEdit}
                  isDisabled={props.saving}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="teal"
                  borderRadius={props.borderRadius}
                  onClick={() => void props.onSaveEdit()}
                  isLoading={props.saving}
                >
                  Save
                </Button>
              </>
            )}
          </HStack>
        </VStack>
      ) : (
        <Box
          m={0}
          fontSize="sm"
          fontWeight="normal"
          whiteSpace="pre-wrap"
          color={props.subtleText}
          fontFamily="var(--font-primary)"
          lineHeight="1.5"
          data-note-body=""
        >
          {props.note.body}
        </Box>
      )}
    </Box>
  );
}
